import { Product } from '@/types/product';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, X, Star, ArrowRight, TrendingUp, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { isFeatureEnabled } from '@/lib/featureFlags';
import { CircleScoreBadge } from '@/components/product/CircleScoreBadge';
import { PromoCodeBadge } from '@/components/product/PromoCodeBadge';
import { useProductPromoCodes } from '@/hooks/usePromoCodes';

interface StructuredComparisonTableProps {
  products: Product[];
}

interface ComparisonRow {
  label: string;
  icon?: React.ReactNode;
  cells: React.ReactNode[];
  category: 'pricing' | 'quality' | 'features' | 'benefits';
}

/**
 * Enhanced comparison table for Compare page
 * Displays: Circle Score, Price, Promo Codes, Ratings, Features, Pros
 */
export function StructuredComparisonTable({ products }: StructuredComparisonTableProps) {
  if (products.length === 0) return null;

  const showCircleScore = isFeatureEnabled('circleScore');
  const showPromoCodes = isFeatureEnabled('promoCodes');

  // Extract features across all products
  const extractFeatures = (product: Product) => {
    if (!product.features) return [];
    
    if (Array.isArray(product.features)) {
      return product.features.map((f: any) => {
        if (typeof f === 'string') return f;
        if (f.title) return f.title;
        return '';
      }).filter(Boolean);
    }
    
    return [];
  };

  const getAllFeatures = () => {
    const featureSet = new Set<string>();
    products.forEach(product => {
      extractFeatures(product).forEach(feature => featureSet.add(feature));
    });
    return Array.from(featureSet);
  };

  const hasFeature = (product: Product, featureName: string) => {
    const features = extractFeatures(product);
    return features.some(f => 
      f.toLowerCase().includes(featureName.toLowerCase()) || 
      featureName.toLowerCase().includes(f.toLowerCase())
    );
  };

  const allFeatures = getAllFeatures();

  // Build comparison rows
  const rows: ComparisonRow[] = [];

  // Quality Section
  if (showCircleScore && products.some(p => p.circleScore)) {
    rows.push({
      label: 'Circle Score',
      icon: <TrendingUp className="h-4 w-4 text-primary" />,
      category: 'quality',
      cells: products.map(p => (
        p.circleScore?.circle_score ? (
          <CircleScoreBadge score={p.circleScore} compact />
        ) : (
          <span className="text-muted-foreground text-sm">N/A</span>
        )
      )),
    });
  }

  rows.push({
    label: 'User Rating',
    icon: <Star className="h-4 w-4 text-yellow-500" />,
    category: 'quality',
    cells: products.map(p => (
      p.rating ? (
        <div className="flex items-center gap-1 justify-center">
          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
          <span className="font-semibold">{p.rating.toFixed(1)}</span>
          <span className="text-xs text-muted-foreground">({p.review_count || 0})</span>
        </div>
      ) : (
        <span className="text-muted-foreground text-sm">No reviews</span>
      )
    )),
  });

  // Pricing Section
  rows.push({
    label: 'Starting Price',
    category: 'pricing',
    cells: products.map(p => (
      p.startingPrice || p.price ? (
        <div className="text-center">
          <div className="text-2xl font-bold text-primary">
            ${(p.startingPrice || p.price)?.toFixed(0)}
          </div>
          <div className="text-xs text-muted-foreground">
            /{p.price_duration || 'mo'}
          </div>
        </div>
      ) : (
        <span className="text-muted-foreground text-sm">Contact for pricing</span>
      )
    )),
  });

  if (showPromoCodes) {
    rows.push({
      label: 'Active Promo',
      icon: <Tag className="h-4 w-4 text-emerald-600" />,
      category: 'pricing',
      cells: products.map(p => <PromoCodeCell productId={p.id} />),
    });
  }

  // Benefits Section (Best For)
  if (products.some(p => p.best_for && p.best_for.length > 0)) {
    rows.push({
      label: 'Best For',
      category: 'benefits',
      cells: products.map(p => {
        const bestFor = p.best_for?.slice(0, 3) || [];
        return bestFor.length > 0 ? (
          <div className="space-y-1 text-left">
            {bestFor.map((item, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <Check className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <span className="line-clamp-2">{item}</span>
              </div>
            ))}
          </div>
        ) : (
          <span className="text-muted-foreground text-sm">Not specified</span>
        );
      }),
    });
  }

  // Features Section
  const featureRows: ComparisonRow[] = allFeatures.slice(0, 10).map(feature => ({
    label: feature,
    category: 'features',
    cells: products.map(p => 
      hasFeature(p, feature) ? (
        <Check className="h-5 w-5 text-emerald-600" />
      ) : (
        <X className="h-5 w-5 text-muted-foreground" />
      )
    ),
  }));

  rows.push(...featureRows);

  // Group rows by category
  const groupedRows = {
    quality: rows.filter(r => r.category === 'quality'),
    pricing: rows.filter(r => r.category === 'pricing'),
    benefits: rows.filter(r => r.category === 'benefits'),
    features: rows.filter(r => r.category === 'features'),
  };

  return (
    <div className="space-y-8">
      {/* Product Headers */}
      <div className="grid gap-4" style={{ gridTemplateColumns: `minmax(180px, 200px) repeat(${products.length}, minmax(250px, 1fr))` }}>
        <div className="font-semibold text-sm text-muted-foreground self-end pb-2">Compare</div>
        {products.map(product => (
          <Card key={product.id} className="border-2 hover:border-primary/50 transition-colors">
            <CardHeader className="space-y-3 pb-4">
              {product.image && (
                <div className="relative w-full h-32 overflow-hidden rounded-lg bg-muted">
                  <img 
                    src={product.image} 
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                  {product.is_select && (
                    <Badge className="absolute top-2 right-2 bg-yellow-500 text-white">
                      SELECT
                    </Badge>
                  )}
                </div>
              )}
              <div>
                <CardTitle className="text-lg mb-1">{product.title}</CardTitle>
                {product.subtitle && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {product.subtitle}
                  </p>
                )}
              </div>
              <Link to={`/product/${product.slug}`}>
                <Button variant="default" size="sm" className="w-full">
                  View Details
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* Quality Section */}
      {groupedRows.quality.length > 0 && (
        <ComparisonSection 
          title="Quality & Reputation" 
          rows={groupedRows.quality} 
          productsCount={products.length}
        />
      )}

      {/* Pricing Section */}
      {groupedRows.pricing.length > 0 && (
        <ComparisonSection 
          title="Pricing & Offers" 
          rows={groupedRows.pricing} 
          productsCount={products.length}
        />
      )}

      {/* Benefits Section */}
      {groupedRows.benefits.length > 0 && (
        <ComparisonSection 
          title="Best Use Cases" 
          rows={groupedRows.benefits} 
          productsCount={products.length}
        />
      )}

      {/* Features Section */}
      {groupedRows.features.length > 0 && (
        <ComparisonSection 
          title="Features" 
          rows={groupedRows.features} 
          productsCount={products.length}
        />
      )}
    </div>
  );
}

interface ComparisonSectionProps {
  title: string;
  rows: ComparisonRow[];
  productsCount: number;
}

function ComparisonSection({ title, rows, productsCount }: ComparisonSectionProps) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-muted/30 border-b">
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="grid" style={{ gridTemplateColumns: `minmax(180px, 200px) repeat(${productsCount}, minmax(250px, 1fr))` }}>
          {rows.map((row, idx) => (
            <div key={idx} className="contents">
              <div className="py-4 px-4 border-b border-r bg-muted/10 font-medium text-sm flex items-center gap-2">
                {row.icon}
                <span>{row.label}</span>
              </div>
              {row.cells.map((cell, cellIdx) => (
                <div 
                  key={cellIdx} 
                  className="py-4 px-4 border-b flex items-center justify-center"
                >
                  {cell}
                </div>
              ))}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Promo code cell with hook
 */
function PromoCodeCell({ productId }: { productId: string }) {
  const { data: promoCodes } = useProductPromoCodes(productId);
  const activePromo = promoCodes?.[0]; // Take first active promo

  if (!activePromo) {
    return <span className="text-muted-foreground text-sm">No active promo</span>;
  }

  return <PromoCodeBadge promo={activePromo} className="text-xs" />;
}
